/**
 * Regroupement des demandes qui partent dans un MÊME email.
 *
 * Le serveur envoie, pour une ligne cliquée, toutes les autres lignes non confirmées du même partenaire
 * pour le même événement (POST /extras-partners/:id/requests/:requestId/send). Décision d'Emeni du
 * 2026-08-22 : « un seul email pour tous les types demandés » quand c'est le même prestataire.
 *
 * Rien ne le disait à l'écran : deux postes chez le même partenaire affichaient deux boutons « Envoyer »
 * identiques, et on croyait légitimement à un doublon (Emeni, 2026-08-24 : « même événement, même
 * partenaire et pourtant deux lignes et deux boutons »). Ce calcul sert à annoncer la portée réelle du
 * bouton.
 *
 * Vit dans un fichier à part parce que DEUX écrans l'utilisent — le suivi global (ExtrasModule) et la fiche
 * partenaire (ExtrasPartnerRequestsPage). Recopié dans chacun, il aurait dérivé, exactement comme un champ
 * ajouté à une seule des deux routes serveur.
 */
export interface EmailBatchRow {
    request_id: string;
    event_id: string | null;
    /** Absent sur la fiche d'un partenaire, où toutes les lignes lui appartiennent déjà. */
    partner_id?: string;
    confirmed_at?: string | null;
    status: 'a_demander' | 'demandee';
}

/** Lignes qui partiront dans le même email que `row` — celle-ci incluse. Miroir exact de la requête serveur. */
export const sameEmailBatch = <T extends EmailBatchRow>(rows: T[], row: T): T[] => {
    // Une demande libre, sans événement rattaché, n'a rien à regrouper.
    if (!row.event_id) return [row];
    const batch = rows.filter((r) => r.event_id === row.event_id
        && (r.partner_id ?? null) === (row.partner_id ?? null)
        && !r.confirmed_at
        && (r.status === 'a_demander' || r.status === 'demandee'));
    return batch.some((r) => r.request_id === row.request_id) ? batch : [row, ...batch];
};

/** Suffixe du bouton : « Envoyer » seule, « Envoyer les 3 demandes » quand le même email en couvre plusieurs. */
export const batchActionLabel = (verb: string, batchSize: number): string =>
    batchSize > 1 ? `${verb} les ${batchSize} demandes` : verb;
